import React from 'react';
import { Shift } from '../../types';
import styles from './ShiftCard.module.css';

interface ShiftCardProps {
  shift: Shift;
  onClockIn?: (id: number) => void;
  onClockOut?: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  showActions?: boolean;
}

/**
 * Card per visualizzare un turno
 */
export const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onClockIn,
  onClockOut,
  onEdit,
  onDelete,
  showActions = true,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      Scheduled: { label: 'Programmato', className: styles.statusScheduled },
      InProgress: { label: 'In Corso', className: styles.statusInProgress },
      Completed: { label: 'Completato', className: styles.statusCompleted },
      Cancelled: { label: 'Cancellato', className: styles.statusCancelled },
    };

    const statusInfo = statusMap[status] || { label: status, className: '' };
    return (
      <span className={`${styles.statusBadge} ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const canClockIn = shift.status === 'Scheduled' && !shift.actualStartTime;
  const canClockOut = shift.status === 'InProgress' && shift.actualStartTime && !shift.actualEndTime;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.role}>{shift.role}</h3>
          <p className={styles.site}>📍 {shift.siteName}</p>
        </div>
        {getStatusBadge(shift.status)}
      </div>

      <div className={styles.body}>
        <div className={styles.infoRow}>
          <span className={styles.label}>👤 Dipendente:</span>
          <span className={styles.value}>{shift.employeeName || 'Non assegnato'}</span>
        </div>

        <div className={styles.infoRow}>
          <span className={styles.label}>🕐 Inizio programmato:</span>
          <span className={styles.value}>{formatDate(shift.startTime)}</span>
        </div>

        <div className={styles.infoRow}>
          <span className={styles.label}>🕐 Fine programmata:</span>
          <span className={styles.value}>{formatDate(shift.endTime)}</span>
        </div>

        {shift.actualStartTime && (
          <div className={styles.infoRow}>
            <span className={styles.label}>▶️ Clock In:</span>
            <span className={styles.value}>{formatDate(shift.actualStartTime)}</span>
          </div>
        )}

        {shift.actualEndTime && (
          <div className={styles.infoRow}>
            <span className={styles.label}>⏸️ Clock Out:</span>
            <span className={styles.value}>{formatDate(shift.actualEndTime)}</span>
          </div>
        )}

        {shift.hoursWorked && (
          <div className={styles.infoRow}>
            <span className={styles.label}>⏱️ Ore lavorate:</span>
            <span className={styles.value}>{shift.hoursWorked.toFixed(2)}h</span>
          </div>
        )}

        {shift.notes && (
          <div className={styles.notes}>
            <strong>Note:</strong> {shift.notes}
          </div>
        )}
      </div>

      {showActions && (
        <div className={styles.actions}>
          {canClockIn && onClockIn && (
            <button
              className={`${styles.button} ${styles.buttonSuccess}`}
              onClick={() => onClockIn(shift.id)}
            >
              ▶️ Clock In
            </button>
          )}

          {canClockOut && onClockOut && (
            <button
              className={`${styles.button} ${styles.buttonWarning}`}
              onClick={() => onClockOut(shift.id)}
            >
              ⏸️ Clock Out
            </button>
          )}

          {onEdit && (
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => onEdit(shift.id)}
            >
              ✏️ Modifica
            </button>
          )}

          {onDelete && shift.status === 'Scheduled' && (
            <button
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={() => onDelete(shift.id)}
            >
              🗑️ Cancella
            </button>
          )}
        </div>
      )}
    </div>
  );
};

ShiftCard.displayName = 'ShiftCard';
